import {
  Badge,
  Box,
  Button,
  Group,
  SimpleGrid,
  Stack,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  DashboardGreeting,
  DashboardStatGrid,
  type DashboardStatTile,
  type QuickAction,
  QuickActionRow,
  RecentActivityFeed,
  resolveGreetingPhrase,
  UpNextCard,
} from '../../components/dashboard';
import { MotionSection, SectionTitle } from '../../components/ui';
import { fetchOk } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { track } from '../../lib/telemetry';

/**
 * /host/dashboard — host landing surface.
 *
 * Same uplift as the artist dashboard: greeting → stat row → up-next card →
 * activity feed + smart quick actions. Hosting concerns swap in: open events,
 * incoming requests, the calendar.
 *
 * Founding-host badge from `/api/dashboard/me` rides as a pill on the
 * greeting hero.
 */
export const Route = createFileRoute('/host/dashboard')({
  component: HostDashboardPage,
});

type HostStats = {
  ok: true;
  activeHat: 'host';
  /** Wave 5: account-level founding-host flag — drives the hero badge. */
  isFoundingHost: boolean;
  /** AUD-023/AUD-031: real venue count + the host's own identity-verification
   *  state, both resolved server-side (see `worker/routes/dashboard.ts`). */
  venueCount: number;
  hostVerificationState: 'approved' | 'pending' | 'rejected' | 'none';
  stats: {
    activeExchanges: number;
    inboxUnread: number;
    hostedToDate: number;
    openEvents: number;
  };
};

function HostDashboardPage() {
  const { user, activeHat, isLoading } = useAuth();
  const first = user ? firstName(user.displayName) : '';

  const enabled = !isLoading && user !== null && activeHat === 'host';
  const statsQuery = useQuery<HostStats>({
    queryKey: ['dashboard-stats', 'host'],
    queryFn: async () => {
      const res = await fetchOk('/api/dashboard/me', { credentials: 'include' });
      const json = (await res.json()) as HostStats;
      return json;
    },
    enabled,
    staleTime: 30_000,
  });

  const stats = statsQuery.data?.stats;
  const isFoundingHost = statsQuery.data?.isFoundingHost ?? false;
  const showSkeletons = enabled && statsQuery.isLoading;

  // Fire dashboard.viewed once per stats load (host hat).
  const viewedRef = useRef<boolean>(false);
  useEffect(() => {
    if (stats === undefined) return;
    if (viewedRef.current) return;
    viewedRef.current = true;
    track('dashboard.viewed', {
      hat: 'host',
      isFoundingHost,
      statsNonZero:
        stats.activeExchanges > 0 ||
        stats.inboxUnread > 0 ||
        stats.hostedToDate > 0 ||
        stats.openEvents > 0,
    });
  }, [stats, isFoundingHost]);

  const greetingWord = resolveGreetingPhrase();
  const greetingLine = first ? `${greetingWord}, ${first}.` : `${greetingWord}.`;
  const statusLine = resolveHostStatusLine(stats);

  // Stat tiles. inboxUnread is the action-relevant tile → accent.
  const tiles: DashboardStatTile[] = [
    {
      label: 'Active exchanges',
      value: stats?.activeExchanges ?? null,
      hint: 'Submitted or confirmed.',
      to: '/inbox?f=active',
    },
    {
      label: 'In the inbox',
      value: stats?.inboxUnread ?? null,
      hint: 'Conversations needing a reply.',
      accent: true,
      to: '/inbox?f=unread',
    },
    {
      label: 'Hosted to date',
      value: stats?.hostedToDate ?? null,
      hint: 'Across every venue.',
      to: '/inbox?f=past',
    },
    {
      label: 'Open events',
      value: stats?.openEvents ?? null,
      hint: 'Published gigs taking interest.',
      to: '/host/events',
    },
  ];

  const actions = resolveHostActions(
    stats,
    statsQuery.data?.venueCount,
    statsQuery.data?.hostVerificationState,
  );

  return (
    <AppShell tone="full">
      {/* Full-width pattern (matches /inbox and /dashboard). Bleed to the
          edges with the same clamped gutter as the AppHeader so the host's
          stat tiles, up-next card and activity feed all breathe on wide
          desktops. */}
      <Box style={{ paddingInline: 'clamp(16px, 4vw, 32px)' }}>
        <Stack gap="xl">
          <MotionSection>
            <DashboardGreeting
              eyebrow="Your venues"
              greeting={greetingLine}
              lede={statusLine}
              pill={
                <Group gap="sm">
                  {isFoundingHost ? (
                    <Badge size="md" variant="light" color="sun" radius="sm">
                      Founding host
                    </Badge>
                  ) : null}
                  <Button
                    component={Link}
                    to="/host/venues/new"
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                    size="sm"
                    radius="md"
                  >
                    Add a venue
                  </Button>
                </Group>
              }
            />
          </MotionSection>

          <MotionSection delay={0.05}>
            <DashboardStatGrid
              tiles={tiles}
              loading={showSkeletons}
              onTileClick={(label) => {
                track('dashboard.stat_clicked', { tile: tileKeyFromLabel(label) });
              }}
            />
          </MotionSection>

          {!isLoading && user !== null ? (
            <MotionSection delay={0.1}>
              <UpNextCard variant="host" />
            </MotionSection>
          ) : null}

          {!isLoading && user !== null ? (
            <MotionSection delay={0.15}>
              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
                <Stack gap="md">
                  <SectionTitle title="Recent activity" />
                  <RecentActivityFeed />
                </Stack>
                <Stack gap="md">
                  <SectionTitle title="Where to go from here" />
                  <QuickActionRow
                    actions={actions}
                    onActionClick={(actionKey, position) => {
                      track('dashboard.quick_action_clicked', { actionKey, position });
                    }}
                  />
                </Stack>
              </SimpleGrid>
            </MotionSection>
          ) : null}
        </Stack>
      </Box>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function resolveHostStatusLine(stats: HostStats['stats'] | undefined): string {
  if (!stats) return 'Your venues, requests, and upcoming stays.';
  if (stats.inboxUnread > 0) {
    const noun = stats.inboxUnread === 1 ? 'request' : 'requests';
    return `${stats.inboxUnread} ${noun} waiting on you.`;
  }
  if (stats.activeExchanges > 0) {
    const noun = stats.activeExchanges === 1 ? 'exchange' : 'exchanges';
    return `${stats.activeExchanges} active ${noun} on the calendar.`;
  }
  return 'New artists are out there discovering you. Keep the calendar fresh.';
}

function resolveHostActions(
  stats: HostStats['stats'] | undefined,
  venueCount: number | undefined,
  hostVerificationState: HostStats['hostVerificationState'] | undefined,
): QuickAction[] {
  const list: QuickAction[] = [];

  // AUD-014/AUD-026: identity verification is the actual publish blocker
  // (see the venue-edit gate) — rank it first whenever the host isn't
  // approved yet, so it can't get bumped out by the 3-slot cap below.
  if (hostVerificationState !== undefined && hostVerificationState !== 'approved') {
    list.push({
      key: 'finish-host-verification',
      title: 'Finish identity verification',
      description:
        hostVerificationState === 'pending'
          ? "Your check is with our team. Most decisions land within 72 hours."
          : hostVerificationState === 'rejected'
            ? 'Your last submission needs another look. Re-submit to unlock publishing.'
            : 'Verification unlocks publishing. Most reviews land within 72 hours.',
      to: '/verification',
      glyph: 'profile',
    });
  }

  if (stats && stats.inboxUnread > 0) {
    list.push({
      key: 'reply-inbox',
      title: `Reply to ${stats.inboxUnread} ${stats.inboxUnread === 1 ? 'request' : 'requests'}`,
      description: 'Open the inbox and keep them moving.',
      to: '/inbox',
      glyph: 'reply',
    });
  } else {
    list.push({
      key: 'open-inbox',
      title: 'Open inbox',
      description: 'Incoming requests and conversations.',
      to: '/inbox',
      glyph: 'inbox',
    });
  }

  // AUD-023: real venue count from `/api/dashboard/me` (replaces the old
  // zero-exchanges-and-events proxy) — a host stuck in review with a venue
  // already on file must stop being told to add their first one.
  const hasNoVenues = venueCount === 0;

  if (hasNoVenues) {
    list.push({
      key: 'add-venue',
      title: 'Add your first venue',
      description: 'Photos and the basics start your venue — verification unlocks publishing.',
      to: '/host/venues/new',
      glyph: 'venue',
    });
  } else {
    list.push({
      key: 'manage-venues',
      title: 'Your venues',
      description: 'Edit venues, photos, calendar, house rules.',
      to: '/host/venues',
      glyph: 'venue',
    });
  }

  const preferAvailability =
    !hasNoVenues &&
    stats !== undefined &&
    (stats.openEvents > 0 || stats.activeExchanges > 0) &&
    stats.inboxUnread === 0;

  if (preferAvailability) {
    list.push({
      key: 'edit-availability',
      title: 'Add open weeks',
      description: "Tell artists which weeks you've got open.",
      to: '/host/availability',
      glyph: 'event',
    });
  } else {
    list.push({
      key: 'post-event',
      title: 'Post an event',
      description: 'A line on the calendar pulls artists in.',
      to: '/host/events/new',
      glyph: 'event',
    });
  }

  return list.slice(0, 3);
}

function firstName(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

function tileKeyFromLabel(label: string): string {
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return slug || 'unknown';
}
